// @flow

import { selectPricingInfos } from '@gandi/billing';

import { useResourceFetch } from '@gandi/resource/react';

import { selectShopCurrent } from 'src/modules/organization/selectors';
import { tldPricesResource } from 'src/modules/tldpublic/tlds';
import type { TldPriceSummary } from 'src/modules/tldpublic/tlds';

import { useAppSelector } from './useAppSelector';

const eligibleEssentialTlds = [
  '.fr',
  '.ca',
  '.uk',
  '.be',
  '.de',
  '.eu',
  '.nl',
  '.co.uk',
] as const;

const eligibleGrids = ['A', 'B', 'C', 'D', 'E'] as const;

function useIsRetailCustomer(): boolean {
  const cartOwnerOrga = useAppSelector(selectShopCurrent);
  const { grid } = useAppSelector(selectPricingInfos);
  const isRetail =
    // Unauthenticated users are eligible
    !cartOwnerOrga ||
    // Authenticated users are eligible if their billed organization is retail (not corpo/reseller)
    ((!cartOwnerOrga.reseller || !cartOwnerOrga.corporate) &&
      // and doesn't have a negotiated pricing grid
      eligibleGrids.includes(grid));

  return isRetail;
}

/** Checks if a domain's TLD is present in a set of eligible TLDs. */
export function isTldEligible(
  domain: string,
  eligibleTldsData: Array<TldPriceSummary>,
): boolean {
  const eligibleTlds = new Set(
    eligibleTldsData.flatMap(tld => [tld.name, tld.alabel].filter(Boolean)),
  );

  // Get all possible TLDs from the domain (for example "example.co.uk" will yield "uk" and "co.uk")
  const potentialTlds = domain
    .split('.')
    .map((_, index, parts) => parts.slice(index).join('.'))
    .slice(1); // Exclude the full domain itself

  return potentialTlds.some(potentialTld => eligibleTlds.has(potentialTld));
}

function useIsEligibleTld(fqdn: string): boolean {
  const { summaries: hostingPackTlds } = useResourceFetch(
    tldPricesResource,
    {
      pack: 'website',
    },
    { enabled: true },
  );

  if (!hostingPackTlds) {
    // If the API is not available, we fallback to a basic check
    return (
      fqdn.endsWith('.com') ||
      fqdn.endsWith('.net') ||
      fqdn.endsWith('.org') ||
      fqdn.endsWith('.fr')
      // WARNING: Do we need to add `eligibleEssentialTlds`?
    );
  }

  return !!hostingPackTlds && isTldEligible(fqdn, hostingPackTlds);
}

/** Temporary frontend-side check until ready from the API:
 *  https://gitlab.corp.gandi.net/gandi/squad-shop/external-issues/-/issues/83
 */
export function useIsEligibleForXsell(fqdn: string): {
  isEligibleTld: boolean,
  isRetail: boolean,
  /** True if all eligibility conditions are true */
  isEligibleForXsell: boolean,
} {
  const isRetail = useIsRetailCustomer();
  const isEligibleTld = useIsEligibleTld(fqdn);

  const isEligibleForXsell = isRetail && isEligibleTld;

  return {
    isRetail,
    isEligibleTld,
    isEligibleForXsell,
  };
}

/**
 * @private `eligibleEssentialTlds` tests only the TLD
 * it is exported for test unit purpose
 * prefer useIsEligibleForEssential for real cases
 */
export function eligibleForEssential(fqdn: string): boolean {
  return eligibleEssentialTlds.some(tld => fqdn.endsWith(tld));
}

export function useIsEligibleForEssential(fqdn: string): boolean {
  const isRetail = useIsRetailCustomer();
  return isRetail && eligibleForEssential(fqdn);
}
